import React, { useMemo } from 'react';
import ReactApexChart from 'react-apexcharts';
import { ApexOptions } from 'apexcharts';
import { formatValueAsString } from '../../../../../utils/helpers';
import { TPolygonCallsItem } from '../../../kpiTypes';
import { ChartContainer } from '../../../../../shared/components/charts/ChartContainer';

interface IChartDataProps {
	data: TPolygonCallsItem[];
}

const PortCallsChartApex: React.FC<IChartDataProps> = ({ data }) => {
	const formatOptions = useMemo(() => ({ minimumFractionDigits: 0, maximumFractionDigits: 2 }), []);

	const transformedData = useMemo(() => {
		return data
			.map((entry) => ({
				startTime: new Date(entry.startTime).toLocaleDateString('default', {
					day: 'numeric',
					month: 'short',
					year: '2-digit',
				}),
				Discharge: entry.portActivity === 'Discharge' ? entry.totalTimeInPort : 0,
				Load: entry.portActivity === 'Load' ? entry.totalTimeInPort : 0,
				Unclassified: entry.portActivity === 'Unclassified' ? entry.totalTimeInPort : 0,
				portName: entry.portName,
			}))
			.filter((item) => item.Discharge > 0 || item.Load > 0 || item.Unclassified > 0);
	}, [data]);

	const series = useMemo<ApexAxisChartSeries>(() => {
		return [
			{
				name: 'Discharge',
				data: transformedData.map((item) => item.Discharge),
				color: '#DF6721',
			},
			{
				name: 'Load',
				data: transformedData.map((item) => item.Load),
				color: '#569FD3',
			},
			{
				name: 'Unclassified',
				data: transformedData.map((item) => item.Unclassified),
				color: '#819391',
			},
		];
	}, [transformedData]);

	const options = useMemo<ApexOptions>(() => {
		return {
			chart: {
				type: 'bar',
				stacked: true,
				toolbar: {
					show: false,
				},
				animations: {
					enabled: false,
				},
				height: '100%',
				width: '100%',
			},
			xaxis: {
				categories: transformedData.map((item) =>
					new Date(item.startTime).toLocaleDateString('default', {
						day: 'numeric',
						month: 'short',
						year: '2-digit',
					}),
				),
				labels: {
					rotateAlways: true,
					rotate: -90,
				},
			},
			yaxis: {
				title: {
					text: 'Time (Days)',
				},
				labels: {
					formatter: (value: any) => formatValueAsString(value, formatOptions, '', '', ''),
				},
			},
			dataLabels: {
				enabled: true,
				formatter: (_value, { dataPointIndex }) => transformedData[dataPointIndex].portName,
				style: {
					fontSize: '12px',
					colors: ['#fff', '#fff', '#fff'],
					fontWeight: 'bold',
					fontFamily: 'Arial, sans-serif',
				},
			},
			tooltip: {
				y: {
					formatter: (value: number, { dataPointIndex }: any) => {
						return `${new Date(transformedData[dataPointIndex].startTime).toLocaleDateString('default', {
							day: 'numeric',
							month: 'short',
							year: '2-digit',
						})} : ${formatValueAsString(value, formatOptions, '', '', '')} `;
					},
					title: {
						formatter: () => ``,
					},
				},
				x: {
					formatter: (_: number, { dataPointIndex }: any) => `${transformedData[dataPointIndex].portName}`,
				},
			},
			legend: {
				position: 'top',
				markers: {
					shape: 'circle',
					size: 8,
				},
				fontSize: '14px',
			},
			plotOptions: {
				bar: {
					horizontal: false,
					columnWidth: '90%',
					dataLabels: {
						orientation: 'vertical',
						position: 'center',
						hideOverflowingLabels: true,
					},
				},
			},
			fill: {
				opacity: 1,
			},
		};
	}, [transformedData, formatOptions]);

	return (
		<ChartContainer data={transformedData} filename="port_calls_data" buttonPosition="top-right">
			<ReactApexChart options={options} series={series} type="bar" width={'100%'} height={'100%'} />
		</ChartContainer>
	);
};

export default PortCallsChartApex;
